import useSWR from "swr";
import { AxiosError } from "axios";
import useUser from "../context/userHook";
import useBoardAndItems from "../context/useBoardAndItems";
import { getItemLocations } from "../../utils/requests/itemRequests";
import { useNavigate } from "react-router-dom";
import useTokenHook from "../context/useTokenHook";
import logger from "../../lib/logger";
import { TableDataInterface, LinkedBoard, Column } from "../../components/ItemViewSection/table.data.interface";

interface ApiErrorResponse {
  message?: string;
}

/** Swaps each column's board ids for {boardId, boardName}, keeping only the item's boards. */
const normalizeItemLocations = (data: TableDataInterface | null | undefined) => {
  if (!data?.linkedBoards || !data.columns) return;
  const linkedBoardMap = new Map<string, string>(data.linkedBoards.map((board: LinkedBoard) => [board.boardId, board.boardName]));

  data.columns.forEach((column: Column) => {
    // Already normalised, e.g. a column kept from an earlier fetch.
    if (column.boards.some((board) => typeof board !== "string")) return;
    const initialBoards = column.boards as string[]; // Cast to string array
    column.boards = initialBoards
      .filter((boardId: string) => linkedBoardMap.has(boardId))
      .map((boardId: string) => ({
        boardId: boardId, // Ensure this is a string
        boardName: linkedBoardMap.get(boardId) || "", // Fallback to empty string
      }));
  });
};

const useItemLocations = () => {
  const { userId } = useUser();
  const { itemIds, currentBoard } = useBoardAndItems();
  const { token } = useTokenHook();

  const navigate = useNavigate();

  const { isLoading, error, data, mutate, isValidating } = useSWR<TableDataInterface | null, AxiosError<ApiErrorResponse>>(
    token && userId ? `itemLocations:${userId}` : null,
    () => getItemLocations(token, itemIds || [], currentBoard, userId),
    {
      revalidateOnFocus: false,
      // Transform the data to match the required format
      onSuccess: normalizeItemLocations,
      onError: (error: AxiosError<ApiErrorResponse>) => {
        if (error?.response?.status === 401 && error?.response?.data?.message === "User not found") {
          navigate("/onboarding");
        }
      },

      onErrorRetry: (error: AxiosError<ApiErrorResponse>) => {
        if (error?.response?.status === 401 && error?.response?.data?.message === "User not found") {
          navigate("/onboarding");
        }
      },
    },
  );

  /**
   * Refetches without ever raising `error`: a failed refresh keeps what's on screen instead of
   * swapping the whole item view for its error state. Used after an item view edit, when the
   * view already shows the saved value.
   */
  const refreshItemLocationsQuietly = () =>
    mutate(
      async (current) => {
        try {
          const fresh = await getItemLocations(token, itemIds || [], currentBoard, userId);
          normalizeItemLocations(fresh);
          return fresh ?? current;
        } catch (refreshError) {
          logger.warn("Quiet item locations refresh failed; keeping the current view", { error: refreshError });
          return current;
        }
      },
      { revalidate: false },
    );

  return {
    isLoading,
    error: error,
    tableData: data || null,
    itemIds: itemIds || [],
    mutateItemLocations: mutate,
    refreshItemLocationsQuietly,
    isValidating,
  };
};

export default useItemLocations;
